/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * FastAPI
 * OpenAPI spec version: 0.1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query'
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import { mutationOptionsWithInteraction } from '../../mutationOptions'
import { customInstance } from '../../mutator'
import type {
  AgentChatStatusDTO,
  AgentRunAuthorizeDTO,
  AgentRunAuthorizedDTO,
  AgentThreadListDTO,
  AgentThreadMessagesDTO,
  AgentThreadUsageDTO,
  GetChatStatusParams,
  HTTPValidationError,
  ListThreadsParams,
} from '../fastAPI.schemas'

/**
 * Authorise one chat turn and mint its run token.

Called by the Copilot runtime with the user's browser token before it
forwards a run to the agent service. Checks, in order: project
membership, the organisation's monthly quota, thread ownership. A turn
that answers an approval interrupt carries the AG-UI ``resume`` entries;
they become the new run's decisions (``approvals.record_decisions``). No
KI-Zugriff grant is created or touched: the run token is scoped to this
run's project by the run itself (see the module docstring), so the
user's grants -- and with them every MCP client's access -- stay exactly
as the user set them.
 * @summary Authorize Run
 */
export const authorizeRun = (agentRunAuthorizeDTO: AgentRunAuthorizeDTO, signal?: AbortSignal) => {
  return customInstance<AgentRunAuthorizedDTO>({
    url: `/agent-runs/authorize`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: agentRunAuthorizeDTO,
    signal,
  })
}

export const useAuthorizeRunMutationOptions = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof authorizeRun>>,
    TError,
    { data: AgentRunAuthorizeDTO },
    TContext
  >
}): UseMutationOptions<
  Awaited<ReturnType<typeof authorizeRun>>,
  TError,
  { data: AgentRunAuthorizeDTO },
  TContext
> => {
  const mutationKey = ['authorizeRun']
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } }

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof authorizeRun>>,
    { data: AgentRunAuthorizeDTO }
  > = props => {
    const { data } = props ?? {}

    return authorizeRun(data)
  }

  const customOptions = mutationOptionsWithInteraction(
    { ...mutationOptions, mutationFn },
    { url: `/agent-runs/authorize` },
    { operationId: 'authorize_run_agent_runs_authorize_post', operationName: 'authorizeRun' },
  )

  return customOptions
}

export type AuthorizeRunMutationResult = NonNullable<Awaited<ReturnType<typeof authorizeRun>>>
export type AuthorizeRunMutationBody = AgentRunAuthorizeDTO
export type AuthorizeRunMutationError = HTTPValidationError

/**
 * @summary Authorize Run
 */
export const useAuthorizeRun = <TError = HTTPValidationError, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof authorizeRun>>,
    TError,
    { data: AgentRunAuthorizeDTO },
    TContext
  >
}): UseMutationResult<
  Awaited<ReturnType<typeof authorizeRun>>,
  TError,
  { data: AgentRunAuthorizeDTO },
  TContext
> => {
  const mutationOptions = useAuthorizeRunMutationOptions(options)

  return useMutation(mutationOptions)
}
/**
 * @summary Get Chat Status
 */
export const getChatStatus = (params?: GetChatStatusParams, signal?: AbortSignal) => {
  return customInstance<AgentChatStatusDTO>({
    url: `/agent-runs/status`,
    method: 'GET',
    params,
    signal,
  })
}

export const getGetChatStatusQueryKey = (params?: GetChatStatusParams) => {
  return [`/agent-runs/status`, ...(params ? [params] : [])] as const
}

export const getGetChatStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof getChatStatus>>,
  TError = HTTPValidationError,
>(
  params?: GetChatStatusParams,
  options?: { query?: UseQueryOptions<Awaited<ReturnType<typeof getChatStatus>>, TError, TData> },
) => {
  const { query: queryOptions } = options ?? {}

  const queryKey = queryOptions?.queryKey ?? getGetChatStatusQueryKey(params)

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getChatStatus>>> = ({ signal }) =>
    getChatStatus(params, signal)

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getChatStatus>>,
    TError,
    TData
  > & { queryKey: QueryKey }
}

export type GetChatStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getChatStatus>>>
export type GetChatStatusQueryError = HTTPValidationError

/**
 * @summary Get Chat Status
 */

export function useGetChatStatus<
  TData = Awaited<ReturnType<typeof getChatStatus>>,
  TError = HTTPValidationError,
>(
  params?: GetChatStatusParams,
  options?: { query?: UseQueryOptions<Awaited<ReturnType<typeof getChatStatus>>, TError, TData> },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetChatStatusQueryOptions(params, options)

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

  query.queryKey = queryOptions.queryKey

  return query
}

/**
 * @summary List Threads
 */
export const listThreads = (params: ListThreadsParams, signal?: AbortSignal) => {
  return customInstance<AgentThreadListDTO>({
    url: `/agent-runs/threads`,
    method: 'GET',
    params,
    signal,
  })
}

export const getListThreadsQueryKey = (params?: ListThreadsParams) => {
  return [`/agent-runs/threads`, ...(params ? [params] : [])] as const
}

export const getListThreadsQueryOptions = <
  TData = Awaited<ReturnType<typeof listThreads>>,
  TError = HTTPValidationError,
>(
  params: ListThreadsParams,
  options?: { query?: UseQueryOptions<Awaited<ReturnType<typeof listThreads>>, TError, TData> },
) => {
  const { query: queryOptions } = options ?? {}

  const queryKey = queryOptions?.queryKey ?? getListThreadsQueryKey(params)

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listThreads>>> = ({ signal }) =>
    listThreads(params, signal)

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listThreads>>,
    TError,
    TData
  > & { queryKey: QueryKey }
}

export type ListThreadsQueryResult = NonNullable<Awaited<ReturnType<typeof listThreads>>>
export type ListThreadsQueryError = HTTPValidationError

/**
 * @summary List Threads
 */

export function useListThreads<
  TData = Awaited<ReturnType<typeof listThreads>>,
  TError = HTTPValidationError,
>(
  params: ListThreadsParams,
  options?: { query?: UseQueryOptions<Awaited<ReturnType<typeof listThreads>>, TError, TData> },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListThreadsQueryOptions(params, options)

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

  query.queryKey = queryOptions.queryKey

  return query
}

/**
 * @summary Get Thread Messages
 */
export const getThreadMessages = (threadId: string, signal?: AbortSignal) => {
  return customInstance<AgentThreadMessagesDTO>({
    url: `/agent-runs/threads/${threadId}/messages`,
    method: 'GET',
    signal,
  })
}

export const getGetThreadMessagesQueryKey = (threadId?: string) => {
  return [`/agent-runs/threads/${threadId}/messages`] as const
}

export const getGetThreadMessagesQueryOptions = <
  TData = Awaited<ReturnType<typeof getThreadMessages>>,
  TError = HTTPValidationError,
>(
  threadId: string,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getThreadMessages>>, TError, TData>
  },
) => {
  const { query: queryOptions } = options ?? {}

  const queryKey = queryOptions?.queryKey ?? getGetThreadMessagesQueryKey(threadId)

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getThreadMessages>>> = ({ signal }) =>
    getThreadMessages(threadId, signal)

  return { queryKey, queryFn, enabled: !!threadId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getThreadMessages>>,
    TError,
    TData
  > & { queryKey: QueryKey }
}

export type GetThreadMessagesQueryResult = NonNullable<
  Awaited<ReturnType<typeof getThreadMessages>>
>
export type GetThreadMessagesQueryError = HTTPValidationError

/**
 * @summary Get Thread Messages
 */

export function useGetThreadMessages<
  TData = Awaited<ReturnType<typeof getThreadMessages>>,
  TError = HTTPValidationError,
>(
  threadId: string,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getThreadMessages>>, TError, TData>
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetThreadMessagesQueryOptions(threadId, options)

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

  query.queryKey = queryOptions.queryKey

  return query
}

/**
 * What this chat has cost so far, shown in the chat popup.
 * @summary Get Thread Usage
 */
export const getThreadUsage = (threadId: string, signal?: AbortSignal) => {
  return customInstance<AgentThreadUsageDTO>({
    url: `/agent-runs/threads/${threadId}/usage`,
    method: 'GET',
    signal,
  })
}

export const getGetThreadUsageQueryKey = (threadId?: string) => {
  return [`/agent-runs/threads/${threadId}/usage`] as const
}

export const getGetThreadUsageQueryOptions = <
  TData = Awaited<ReturnType<typeof getThreadUsage>>,
  TError = HTTPValidationError,
>(
  threadId: string,
  options?: { query?: UseQueryOptions<Awaited<ReturnType<typeof getThreadUsage>>, TError, TData> },
) => {
  const { query: queryOptions } = options ?? {}

  const queryKey = queryOptions?.queryKey ?? getGetThreadUsageQueryKey(threadId)

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getThreadUsage>>> = ({ signal }) =>
    getThreadUsage(threadId, signal)

  return { queryKey, queryFn, enabled: !!threadId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getThreadUsage>>,
    TError,
    TData
  > & { queryKey: QueryKey }
}

export type GetThreadUsageQueryResult = NonNullable<Awaited<ReturnType<typeof getThreadUsage>>>
export type GetThreadUsageQueryError = HTTPValidationError

/**
 * @summary Get Thread Usage
 */

export function useGetThreadUsage<
  TData = Awaited<ReturnType<typeof getThreadUsage>>,
  TError = HTTPValidationError,
>(
  threadId: string,
  options?: { query?: UseQueryOptions<Awaited<ReturnType<typeof getThreadUsage>>, TError, TData> },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetThreadUsageQueryOptions(threadId, options)

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

  query.queryKey = queryOptions.queryKey

  return query
}
